/**
 * #40 · Detalle de un rollo: dónde está en la cadena, qué le pasó y cómo se
 * lo mueve.
 *
 * Tres bloques, en este orden y por este motivo:
 *
 *  1. **La cadena** (`CustodyChain`) — dónde está hoy dentro de
 *     `ordered → printed → shipped → received → in_use → consumed`. Es un
 *     diagrama, no un control: el estado no se elige, se llega a él.
 *  2. **Mover el rollo** — un formulario que sólo ofrece los destinos
 *     legítimos desde el estado actual. `stolen` nunca está entre ellos.
 *  3. **El historial** (`CustodyTimeline`) — append-only, con la evidencia de
 *     cada evento.
 *
 * La denuncia de robo está aparte, en rojo y con su propia confirmación de dos
 * pasos: no es una transición más de un desplegable.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useApiQuery } from '@/api/hooks';
import { rollKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { cn } from '@/ui/cn';
import { ErrorState, QueryBoundary } from '@/ui/states';
import { DetailList, DetailRow, SelectField, TextAreaField, TextField } from '../products/ui/fields';
import { CustodyTimeline } from './CustodyTimeline';
import {
  CUSTODY_CHAIN,
  ROLL_STATE_LABEL_KEYS,
  ROLL_STATE_TONES,
  isRollState,
  serialRange,
  transitionTargets,
  type RollState,
} from './custody';
import { LabelsTabs } from './LabelsTabs';
import { RollStolenDialog } from './RollStolenDialog';

type Roll = Schemas['RollOut'];
type TransitionOut = Schemas['TransitionOut'];

interface TransitionDraft {
  to_state: RollState;
  location: string | null;
  reference: string | null;
  note: string | null;
  carrier: string | null;
  tracking_ref: string | null;
}

export function RollDetailPage(): ReactNode {
  const { t, fmt } = useI18n();
  const { can } = useAuth();
  const api = useApi();
  const queryClient = useQueryClient();
  const params = useParams<{ rollId: string }>();
  const rollId = Number(params.rollId);
  const valid = Number.isInteger(rollId) && rollId > 0;

  const [stolenOpen, setStolenOpen] = useState(false);

  const roll = useApiQuery<Roll>(
    rollKeys.detail(rollId),
    `/api/v1/rolls/${String(rollId)}`,
    undefined,
    { enabled: valid },
  );

  const move = useMutation<TransitionOut, unknown, TransitionDraft>({
    mutationFn: (draft) =>
      api.post<TransitionOut>(`/api/v1/rolls/${String(rollId)}/transition`, draft),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rollKeys.all });
    },
  });

  if (!valid) {
    return (
      <div className="mx-auto max-w-4xl">
        <Card>
          <CardBody>
            <ErrorState error={new Error(t('labels.roll.badId'))} />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <LabelsTabs active="rolls" />
      <Link
        to="/labels/rolls"
        className="inline-block text-sm text-accent underline underline-offset-2"
      >
        {t('labels.rolls.back')}
      </Link>

      <QueryBoundary query={roll}>
        {(data) => {
          const state: RollState | null = isRollState(data.state) ? data.state : null;
          const targets = state === null ? [] : transitionTargets(state);
          const code = data.roll_code ?? `#${String(data.id)}`;

          return (
            <>
              {state === 'stolen' && (
                <div
                  role="alert"
                  className="rounded-(--radius-card) border border-critical/30 bg-critical-soft p-5"
                >
                  <p className="text-sm font-semibold text-critical">
                    {t('labels.stolen.banner')}
                  </p>
                  <p className="mt-1 text-sm text-ink">
                    {t('labels.stolen.banner.detail', {
                      when: fmt.dateTime(data.reported_stolen_at),
                    })}
                  </p>
                </div>
              )}

              <Card>
                <CardHeader
                  title={code}
                  description={data.supplier ?? t('labels.roll.noSupplier')}
                  actions={
                    <>
                      <Badge tone={state === null ? 'neutral' : ROLL_STATE_TONES[state]}>
                        {state === null ? data.state : t(ROLL_STATE_LABEL_KEYS[state])}
                      </Badge>
                      {can('admin') && state !== null && state !== 'stolen' && (
                        <Button
                          size="sm"
                          variant="critical"
                          onClick={() => {
                            setStolenOpen(true);
                          }}
                        >
                          {t('labels.stolen.action')}
                        </Button>
                      )}
                    </>
                  }
                />
                <CardBody className="space-y-5">
                  <CustodyChain current={state} />

                  <DetailList>
                    <DetailRow
                      label={t('labels.rolls.column.serials')}
                      value={
                        <code className="font-mono text-xs">
                          {serialRange(data.serial_from, data.serial_to, (value) =>
                            fmt.number(value),
                          ) ?? '—'}
                        </code>
                      }
                    />
                    <DetailRow
                      label={t('labels.rolls.column.units')}
                      value={<span className="tabular-nums">{fmt.number(data.unit_count)}</span>}
                    />
                    <DetailRow
                      label={t('labels.rolls.column.order')}
                      value={
                        data.label_order_id === null || data.label_order_id === undefined ? (
                          '—'
                        ) : (
                          <Link
                            to={`/labels/orders/${String(data.label_order_id)}`}
                            className="text-accent underline underline-offset-2"
                          >
                            #{data.label_order_id}
                          </Link>
                        )
                      }
                    />
                    <DetailRow
                      label={t('labels.roll.index')}
                      value={data.roll_index ?? '—'}
                    />
                    <DetailRow label={t('labels.roll.carrier')} value={data.carrier ?? '—'} />
                    <DetailRow
                      label={t('labels.roll.tracking')}
                      value={
                        data.tracking_ref === null || data.tracking_ref === undefined ? (
                          '—'
                        ) : (
                          <code className="font-mono text-xs">{data.tracking_ref}</code>
                        )
                      }
                    />
                    <DetailRow
                      label={t('labels.roll.printedAt')}
                      value={fmt.dateTime(data.printed_at)}
                    />
                    <DetailRow
                      label={t('labels.roll.shippedAt')}
                      value={fmt.dateTime(data.shipped_at)}
                    />
                    <DetailRow
                      label={t('labels.roll.receivedAt')}
                      value={fmt.dateTime(data.received_at)}
                    />
                    <DetailRow
                      label={t('labels.roll.workerStatus')}
                      value={<code className="font-mono text-xs">{data.worker_status}</code>}
                    />
                  </DetailList>
                </CardBody>
              </Card>

              {can('analyst') && (
                <Card>
                  <CardHeader
                    title={t('labels.transition.title')}
                    description={t('labels.transition.description')}
                  />
                  <CardBody>
                    {targets.length === 0 ? (
                      <p className="text-sm text-ink-muted">
                        {t('labels.transition.terminal', {
                          state: state === null ? data.state : t(ROLL_STATE_LABEL_KEYS[state]),
                        })}
                      </p>
                    ) : (
                      <TransitionForm
                        targets={targets}
                        onSubmit={(draft) => {
                          move.mutate(draft);
                        }}
                        isSubmitting={move.isPending}
                        error={move.error}
                        result={move.data}
                      />
                    )}
                  </CardBody>
                </Card>
              )}

              <Card>
                <CardHeader
                  title={t('labels.custody.title')}
                  description={t('labels.custody.description')}
                />
                <CustodyTimeline rollId={rollId} />
              </Card>

              {stolenOpen && (
                <RollStolenDialog
                  roll={data}
                  onClose={() => {
                    setStolenOpen(false);
                  }}
                />
              )}
            </>
          );
        }}
      </QueryBoundary>
    </div>
  );
}

/**
 * La cadena feliz, con el hito actual resaltado. Los estados de salida
 * (`stolen`, `destroyed`, `voided`) no están en la línea: no son un paso más,
 * son la interrupción de la línea.
 */
function CustodyChain({ current }: { current: RollState | null }): ReactNode {
  const { t } = useI18n();
  const index = current === null ? -1 : CUSTODY_CHAIN.indexOf(current);
  // El estado que interrumpió la cadena, o `null` si el rollo sigue en ella.
  const interrupted = current !== null && index === -1 ? current : null;

  return (
    <div>
      <p className="text-xs font-semibold tracking-wide text-ink-muted uppercase">
        {t('labels.chain.title')}
      </p>
      <ol className="mt-2 flex flex-wrap items-center gap-x-1 gap-y-2">
        {CUSTODY_CHAIN.map((state, position) => {
          const done = index >= 0 && position < index;
          const active = position === index;
          return (
            <li key={state} className="flex items-center gap-1">
              <span
                aria-current={active ? 'step' : undefined}
                className={cn(
                  'rounded border px-2 py-0.5 text-xs font-medium',
                  active && 'border-accent bg-accent text-accent-contrast',
                  done && 'border-verified/30 bg-verified-soft text-verified',
                  !active && !done && 'border-border bg-surface-sunken text-ink-subtle',
                )}
              >
                {t(ROLL_STATE_LABEL_KEYS[state])}
              </span>
              {position < CUSTODY_CHAIN.length - 1 && (
                <span aria-hidden="true" className="text-ink-subtle">
                  ›
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {interrupted !== null && (
        <p className="mt-2 text-sm font-medium text-critical">
          {t('labels.chain.interrupted', { state: t(ROLL_STATE_LABEL_KEYS[interrupted]) })}
        </p>
      )}
    </div>
  );
}

function TransitionForm({
  targets,
  onSubmit,
  isSubmitting,
  error,
  result,
}: {
  targets: readonly RollState[];
  onSubmit: (draft: TransitionDraft) => void;
  isSubmitting: boolean;
  error: unknown;
  result: TransitionOut | undefined;
}): ReactNode {
  const { t, fmt } = useI18n();
  const first = targets[0];
  const [target, setTarget] = useState<RollState>(first ?? 'printed');
  const [location, setLocation] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [carrier, setCarrier] = useState('');
  const [trackingRef, setTrackingRef] = useState('');

  // `carrier` y `tracking_ref` sólo tienen sentido cuando el rollo se despacha.
  const shipping = target === 'shipped';

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    onSubmit({
      to_state: target,
      location: location.trim() === '' ? null : location.trim(),
      reference: reference.trim() === '' ? null : reference.trim(),
      note: note.trim() === '' ? null : note.trim(),
      carrier: shipping && carrier.trim() !== '' ? carrier.trim() : null,
      tracking_ref: shipping && trackingRef.trim() !== '' ? trackingRef.trim() : null,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <SelectField
        label={t('labels.transition.to')}
        hint={t('labels.transition.to.hint')}
        value={target}
        onChange={(value) => {
          if (isRollState(value)) setTarget(value);
        }}
        options={targets.map((state) => ({ value: state, label: t(ROLL_STATE_LABEL_KEYS[state]) }))}
        required
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label={t('labels.transition.location')}
          value={location}
          onChange={setLocation}
          maxLength={200}
        />
        <TextField
          label={t('labels.transition.reference')}
          hint={t('labels.transition.reference.hint')}
          value={reference}
          onChange={setReference}
          maxLength={120}
          mono
        />
      </div>

      {shipping && (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label={t('labels.transition.carrier')}
            value={carrier}
            onChange={setCarrier}
            maxLength={120}
          />
          <TextField
            label={t('labels.transition.tracking')}
            value={trackingRef}
            onChange={setTrackingRef}
            maxLength={120}
            mono
          />
        </div>
      )}

      <TextAreaField
        label={t('labels.transition.note')}
        value={note}
        onChange={setNote}
        rows={2}
        maxLength={500}
      />

      {Boolean(error) && <ErrorState error={error} />}

      {result !== undefined && (
        <p
          role="status"
          className="rounded border border-verified/30 bg-verified-soft px-3 py-2 text-sm text-verified"
        >
          {t('labels.transition.done', {
            to: t(ROLL_STATE_LABEL_KEYS[result.to_state]),
            units: fmt.number(result.units_affected),
          })}
        </p>
      )}

      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? t('form.saving') : t('labels.transition.submit')}
        </Button>
      </div>
    </form>
  );
}
